import type { ResellerMemberRole } from "./resellerAccounts.js";

/**
 * Reseller APPLICATIONS — the contract between the command center's
 * "System Admin ▸ Reseller Applications" surface and crm-api's staff
 * controller (`Api::V1::ResellerApplicationsController`, spec E §7.2).
 *
 * A `ResellerApplication` is the lifecycle record a company gets while it is
 * asking to become a reseller. `resellers` itself has NO status (spec E §2,
 * fact 2): a `Reseller` row is created (or linked) only at activation, when
 * it is by definition live. So everything here is about the row that exists
 * BEFORE a reseller does — and the two verbs that end it, Activate and Reject.
 *
 * The helpers at the bottom are pure and shared by the web page, the BFF's
 * validation and the tests, so "may this go live?" and "which role does a
 * linked person get?" have exactly one definition.
 *
 * Shape = crm-api's `staff_json` (Phase 1 plan, Task 6).
 */

export const RESELLER_APPLICATION_STATUSES = [
  "submitted",
  "documents_submitted",
  "active",
  "rejected",
] as const;
export type ResellerApplicationStatus = (typeof RESELLER_APPLICATION_STATUSES)[number];

export const RESELLER_APPLICATION_STATUS_LABELS: Record<ResellerApplicationStatus, string> = {
  submitted: "Registered",
  documents_submitted: "Documents submitted",
  active: "Activated",
  rejected: "Rejected",
};

/** `open` = `submitted` + `documents_submitted`, crm-api's `ResellerApplication.open` scope. */
export const RESELLER_APPLICATION_STATUS_FILTERS = [
  "open",
  ...RESELLER_APPLICATION_STATUSES,
] as const;
export type ResellerApplicationStatusFilter = (typeof RESELLER_APPLICATION_STATUS_FILTERS)[number];

export const RESELLER_APPLICATION_FILTER_LABELS: Record<ResellerApplicationStatusFilter, string> = {
  open: "Open",
  ...RESELLER_APPLICATION_STATUS_LABELS,
};

export const RESELLER_APPLICATION_DOCUMENT_KINDS = [
  "signed_agreement",
  "signed_nda",
  "countersigned_agreement",
  "countersigned_nda",
] as const;
export type ResellerApplicationDocumentKind = (typeof RESELLER_APPLICATION_DOCUMENT_KINDS)[number];

export const RESELLER_APPLICATION_DOCUMENT_LABELS: Record<ResellerApplicationDocumentKind, string> = {
  signed_agreement: "Reseller Agreement — signed by the applicant",
  signed_nda: "NDA — signed by the applicant",
  countersigned_agreement: "Reseller Agreement — countersigned by XOP",
  countersigned_nda: "NDA — countersigned by XOP",
};

/** The two slots STAFF upload. The other two are the applicant's. */
export const COUNTERSIGN_DOCUMENT_KINDS = ["countersigned_agreement", "countersigned_nda"] as const;
export type CountersignDocumentKind = (typeof COUNTERSIGN_DOCUMENT_KINDS)[number];

export type ResellerApplicationDocument =
  | { attached: false }
  | { attached: true; filename: string; uploaded_at: string };

/** One row of `match_candidates` (spec E §5.1) — computed at registration,
 * stored with its reasons so the review page can explain WHY. */
export interface ResellerMatchCandidate {
  reseller_id: number;
  name: string;
  code: string | null;
  /** 0..1 */
  score: number;
  reasons: string[];
  active_members: number;
  customer_count: number;
  lead_count: number;
}

/** The queue's "top candidate" column: the first candidate, projected. */
export type ResellerTopCandidate = Pick<ResellerMatchCandidate, "reseller_id" | "name" | "code" | "score">;

export interface ResellerApplication {
  id: number;
  status: ResellerApplicationStatus;
  decision: "created" | "linked" | "rejected" | null;
  decision_note: string | null;
  company_name: string;
  website: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  email: string;
  phone: string | null;
  country: string | null;
  message: string | null;
  email_domain: string | null;
  website_domain: string | null;
  reseller: { id: number; name: string; code: string | null } | null;
  decided_by: { id: number; full_name: string } | null;
  documents: Record<ResellerApplicationDocumentKind, ResellerApplicationDocument>;
  documents_submitted_at: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  /** Present on the index and the show (added to `staff_json` by Task 9 of
   * the Phase 3 plan). `null` when the matcher found nothing. Optional so a
   * crm-api that predates the field still renders as "—". */
  top_candidate?: ResellerTopCandidate | null;
  /** Show only (`candidates: true`). */
  match_candidates?: ResellerMatchCandidate[];
  /** Show only: the next free 3-digit code, or null when the pool is spent. */
  suggested_code?: string | null;
}

export interface ListResellerApplicationsResponse {
  applications: ResellerApplication[];
}

export interface ShowResellerApplicationResponse {
  application: ResellerApplication;
}

/** What the browser sends the BFF. The BFF wraps it as crm-api's
 * `{ activation: {...} }`. `role` is REQUIRED on link: the dialog always
 * shows it (spec §5.2) so the reviewer's choice is explicit, never implied. */
export type ActivateResellerApplicationInput =
  | { mode: "create"; name?: string; code?: string }
  | { mode: "link"; reseller_id: number; role: ResellerMemberRole };

export interface ActivateResellerApplicationResponse {
  application: ResellerApplication;
  reseller: { id: number; name: string; code: string | null };
  membership: { id: number; email: string; role: ResellerMemberRole };
}

export interface RejectResellerApplicationInput {
  note: string;
}

/** Same rule as `ResellerNetworkController#create_child` and Phase 1's
 * `ResellerApplication::RESERVED_CODE`. */
export const RESELLER_CODE_RESERVED = "700";
/** crm-api's per-file cap (spec §13.2). */
export const MAX_COUNTERSIGN_PDF_BYTES = 10 * 1024 * 1024;
/** The BFF's multipart body cap (spec §9 / §13.2): two 10 MB PDFs + boundary overhead. */
export const COUNTERSIGN_BODY_LIMIT_BYTES = 25 * 1024 * 1024;

export function isOpenApplication(a: Pick<ResellerApplication, "status">): boolean {
  return a.status === "submitted" || a.status === "documents_submitted";
}

export function countersignComplete(a: Pick<ResellerApplication, "documents">): boolean {
  return COUNTERSIGN_DOCUMENT_KINDS.every((kind) => a.documents[kind].attached);
}

/**
 * Why Activate is disabled, or `null` when it may be clicked. The wording is
 * shown next to the button verbatim, and it deliberately matches crm-api's
 * own refusals (`ResellerApplication#activate!`) so a reviewer who somehow
 * gets past the disabled button reads the same sentence in the error toast.
 */
export function activationBlocker(
  a: Pick<ResellerApplication, "status" | "documents">,
): string | null {
  if (!isOpenApplication(a)) return "This application has already been decided.";
  if (!countersignComplete(a)) return "Upload both countersigned documents before activating.";
  return null;
}

/** Spec §5.2: a company that already has people should not get a second
 * owner from a web form; an empty one gets its first. */
export function defaultLinkRole(candidate: Pick<ResellerMatchCandidate, "active_members">): ResellerMemberRole {
  return candidate.active_members > 0 ? "salesperson" : "owner";
}

export function resellerCodeError(code: string): string | null {
  const trimmed = code.trim();
  if (trimmed === "") return "Enter a 3-digit reseller code.";
  if (!/^\d{3}$/.test(trimmed)) return "The code must be exactly 3 digits.";
  if (trimmed === RESELLER_CODE_RESERVED) return `Code ${RESELLER_CODE_RESERVED} is reserved and cannot be used.`;
  return null;
}

export function applicantName(
  a: Pick<ResellerApplication, "contact_first_name" | "contact_last_name" | "email">,
): string {
  const name = [a.contact_first_name, a.contact_last_name]
    .map((s) => (s ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return name || a.email;
}

/** Client-side mirror of crm-api's `pdf_errors` (type by name or MIME, size
 * ≤ 10 MB). Returns the message to show, or `null` when the file may be sent.
 * crm-api still checks the `%PDF-` magic bytes — this only saves a round trip. */
export function isPdfFile(file: { name: string; type: string; size: number }): string | null {
  const looksPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  if (!looksPdf) return "Only a PDF can be uploaded here.";
  if (file.size > MAX_COUNTERSIGN_PDF_BYTES) return "The PDF must be smaller than 10 MB.";
  return null;
}
